import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { EMPTY, from, Observable, of, switchMap } from 'rxjs';
import { RefreshAuthResultEnum } from '../models/refresh-auth-result.enum';
import { Router } from '@angular/router';
import { BaseAuthService } from '../services/base-auth.service';
import { User } from '../models/user';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly authService = inject(BaseAuthService<User, unknown>);
  private readonly router = inject(Router);

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return from(this.authService.isAccessTokenValid()).pipe(
      // Refresh the token if it's not valid
      switchMap((isValid) => (isValid ? of(RefreshAuthResultEnum.SUCCESS) : from(this.authService.refreshToken()))),
      switchMap((result) => {
        if (result === RefreshAuthResultEnum.SUCCESS) {
          return from(this.authService.getAccessToken());
        }

        return of(null);
      }),
      switchMap((accessToken) => {
        // If no access token is available even after trying to refresh it, go to the login page
        if (!accessToken) {
          this.authService.logout();
          this.router.navigateByUrl('/login');
          return EMPTY;
        }

        const clonedReq = request.clone({
          headers: this.setHeaders(request, accessToken),
        });

        return next.handle(clonedReq);
      }),
    );
  }

  setHeaders(request: HttpRequest<unknown>, authToken: string) {
    return request.headers.set('Authorization', 'Bearer ' + authToken);
  }
}
